import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import { fmtCurrency, fmtCompact, fmtDate, fmtRelative, daysUntil, CATEGORY_COLORS } from "@/lib/format";
import StatCard from "@/components/StatCard";
import EmptyState from "@/components/EmptyState";
import { Link } from "react-router-dom";
import { accountTypeMeta } from "@/lib/investmentTypes";
import { iconForCategoryName } from "@/lib/categoryIcons";
import { useCategories } from "@/contexts/CategoriesContext";
import {
    Wallet, TrendingDown, TrendingUp, PiggyBank, RefreshCw, AlertTriangle,
    ArrowUpRight, ArrowDownRight, Calendar as CalendarIcon, Receipt, Target, Users, User as UserIcon,
    AreaChart as AreaChartIcon, LineChart as LineChartIcon, BarChart3, PieChart as PieChartIcon,
} from "lucide-react";
import {
    Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const INCOME_EXPENSE_CHART_TYPES = [
    { value: "area", label: "Area", icon: AreaChartIcon },
    { value: "bar", label: "Bar", icon: BarChart3 },
    { value: "line", label: "Line", icon: LineChartIcon },
    { value: "pie", label: "Split", icon: PieChartIcon },
];

const PERIODS = [
    { value: "week", label: "Week", short: "Wk" },
    { value: "month", label: "Month", short: "Mo" },
    { value: "year", label: "Year", short: "Yr" },
    { value: "all", label: "All time", short: "All" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const { isMember, memberName } = useHousehold();
    const canViewPro = hasProAccess(user, isMember);
    const { categories } = useCategories();
    const [period, setPeriod] = useState("month");
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [scope, setScope] = useState(() => user?.default_scope || "mine");
    const [ieChartType, setIeChartType] = useState("bar");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [budgets, setBudgets] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [markingPaid, setMarkingPaid] = useState(null);
    // Bill ids the user said "not yet" to — silences the confirm popup for the rest of
    // this session (not persisted); the bill still shows, overdue, in the card below.
    const [dismissedBillIds, setDismissedBillIds] = useState([]);

    const fetchDashboard = useCallback(() => {
        const params = { period };
        if (isMember) params.scope = scope;
        return api.get("/dashboard", { params }).then(({ data }) => setData(data));
    }, [period, scope, isMember]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetchDashboard().finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [fetchDashboard]);

    const markBillPaid = async (id) => {
        try {
            setMarkingPaid(id);
            await api.post(`/expenses/${id}/mark-paid`);
            toast.success("Marked as paid");
            await fetchDashboard();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to mark as paid");
        } finally {
            setMarkingPaid(null);
        }
    };

    useEffect(() => {
        let mounted = true;
        api.get("/budgets").then(({ data }) => mounted && setBudgets(data)).catch(() => {});
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (!canViewPro) { setInvestments([]); return; }
        let mounted = true;
        const params = {};
        if (isMember) params.scope = scope;
        api.get("/investments", { params }).then(({ data }) => mounted && setInvestments(data)).catch(() => {});
        return () => { mounted = false; };
    }, [scope, isMember, canViewPro]);

    const cur = user?.currency || "USD";
    const series = data?.income_vs_expense_series || [];
    // Income vs expense "Split" pie — totals for the selected period (week/month/year/all),
    // not the fixed 6-month series, so it always matches the stat cards above it.
    const ieSplitData = useMemo(() => {
        const income = parseFloat(data?.total_income) || 0;
        const expense = parseFloat(data?.total_expense) || 0;
        return [
            { name: "Income", value: income, fill: "#10B981" },
            { name: "Expenses", value: expense, fill: "#6366F1" },
        ].filter(d => d.value > 0);
    }, [data]);
    const ieSplitTotal = ieSplitData.reduce((a, b) => a + b.value, 0);
    const catData = useMemo(
        () => (data?.spending_by_category || []).map(c => ({ name: c.category, value: parseFloat(c.total) })),
        [data]
    );
    const totalCat = catData.reduce((a, b) => a + b.value, 0);

    // Overdue bills — due date has passed and it hasn't been marked paid (or dismissed
    // this session). Surfaced one at a time as a confirm popup; "Not yet" silences it
    // until the next visit, "Yes" marks it paid via the same mark-paid endpoint.
    const overdueBills = (data?.upcoming_bills || []).filter(
        b => daysUntil(b.next_due_date) < 0 && !dismissedBillIds.includes(b.id)
    );
    const currentOverdueBill = overdueBills[0] || null;

    return (
        <div className="space-y-4" data-testid="dashboard-page">
            {/* Overdue bill confirm popup — one at a time, re-checked as each is resolved */}
            <AlertDialog open={!!currentOverdueBill}>
                <AlertDialogContent data-testid="overdue-bill-dialog">
                    <AlertDialogHeader>
                        <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 grid place-items-center mb-1">
                            <AlertTriangle className="w-5 h-5" />
                        </div>
                        <AlertDialogTitle>Did you pay this bill?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold text-slate-900">{currentOverdueBill?.title}</span> ({fmtCurrency(currentOverdueBill?.amount, cur)}) was due {currentOverdueBill ? fmtDate(currentOverdueBill.next_due_date) : ""} and hasn't been marked as paid yet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="overdue-bill-not-yet"
                            onClick={() => setDismissedBillIds(ids => [...ids, currentOverdueBill.id])}
                        >
                            Not yet
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="overdue-bill-confirm-paid"
                            disabled={markingPaid === currentOverdueBill?.id}
                            onClick={() => markBillPaid(currentOverdueBill.id)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {markingPaid === currentOverdueBill?.id ? "Saving…" : "Yes, mark paid"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Header row */}
            <div className="flex flex-row items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 truncate">Hi, {user?.name?.split(" ")[0] || "there"}</p>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    {isMember ? (
                        <Tabs value={scope} onValueChange={setScope}>
                            <TabsList data-testid="dash-scope-tabs" className="bg-white border border-slate-200/80 rounded-xl p-0.5 sm:p-1 h-auto">
                                <TabsTrigger value="household" data-testid="dash-scope-household" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-2 sm:px-3 h-6 sm:h-9 gap-1">
                                    <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline text-sm">Combined</span>
                                </TabsTrigger>
                                <TabsTrigger value="mine" data-testid="dash-scope-mine" className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-2 sm:px-3 h-6 sm:h-9 gap-1">
                                    <UserIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline text-sm">Just me</span>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <Tabs value={period} onValueChange={setPeriod} className="flex-shrink-0">
                        <TabsList data-testid="period-tabs" className="bg-white border border-slate-200/80 rounded-xl p-0.5 sm:p-1 h-auto">
                            {PERIODS.map(p => (
                                <TabsTrigger key={p.value} value={p.value} data-testid={`period-${p.value}`} className="rounded-lg data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-2 sm:px-4 h-6 sm:h-9 text-[11px] sm:text-sm">
                                    <span className="sm:hidden">{p.short}</span>
                                    <span className="hidden sm:inline">{p.label}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Stat cards — mobile: compact 2x2 grid (all four sized like Income/Expense) so charts land in the first view. sm/lg: unchanged 2x2 / 4-across, full size. */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                <div className="animate-fade-up"><StatCard colorful compact testid="stat-balance" label="Available Balance" value={fmtCurrency(data?.total_balance, cur)} sublabel="Across income & expenses" accent="indigo" icon={Wallet} /></div>
                <div className="animate-fade-up delay-50"><StatCard colorful compact testid="stat-income" label="Income (this period)" value={fmtCurrency(data?.total_income, cur)} sublabel="Money in" accent="emerald" icon={TrendingUp} /></div>
                <div className="animate-fade-up delay-100"><StatCard colorful compact testid="stat-expense" label="Expenses (this period)" value={fmtCurrency(data?.total_expense, cur)} sublabel="Money out" accent="rose" icon={TrendingDown} /></div>
                <div className="animate-fade-up delay-150"><StatCard colorful compact testid="stat-savings" label="Total savings" value={fmtCurrency(data?.total_savings, cur)} sublabel={`${(data?.sinking_funds || []).length} sinking funds`} accent="violet" icon={PiggyBank} /></div>
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-elev rounded-2xl p-6 lg:col-span-2 animate-fade-up delay-200" data-testid="income-expense-chart">
                    <div className="flex items-start sm:items-end justify-between mb-4 gap-3">
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Income vs expenses</h3>
                            <p className="text-sm text-slate-500">{ieChartType === "pie" ? "This period's split" : "Last 6 months"}</p>
                        </div>
                        {/* Mobile: dropdown, pinned top-right in line with the title. Desktop (sm+): unchanged icon-button row. */}
                        <Select value={ieChartType} onValueChange={setIeChartType}>
                            <SelectTrigger data-testid="ie-chart-type-select-mobile" className="sm:hidden h-8 rounded-lg w-[92px] px-2 text-xs flex-shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {INCOME_EXPENSE_CHART_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <div className="hidden sm:inline-flex bg-slate-100 rounded-xl p-1 gap-0.5 flex-shrink-0" data-testid="ie-chart-type-tabs">
                            {INCOME_EXPENSE_CHART_TYPES.map(ct => {
                                const Icon = ct.icon;
                                const active = ieChartType === ct.value;
                                return (
                                    <button
                                        key={ct.value} onClick={() => setIeChartType(ct.value)} data-testid={`ie-chart-type-${ct.value}`}
                                        title={ct.label}
                                        className={`p-1.5 rounded-lg transition-all ${active ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="h-64">
                        {ieChartType === "pie" ? (
                            ieSplitData.length === 0 ? (
                                <div className="h-full grid place-items-center text-sm text-slate-400">No activity this period yet.</div>
                            ) : (
                                <div className="h-full flex flex-col sm:flex-row items-center gap-4">
                                    <div className="relative flex-1 h-full max-h-64 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={ieSplitData} dataKey="value" nameKey="name" innerRadius={64} outerRadius={100} paddingAngle={2.5} stroke="none">
                                                    {ieSplitData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                                </Pie>
                                                <Tooltip formatter={(v) => fmtCurrency(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                            <div className="text-center">
                                                <div className="text-xs text-slate-500">Net</div>
                                                <div className="font-display text-xl font-extrabold text-slate-900">{fmtCompact((data?.total_income || 0) - (data?.total_expense || 0), cur)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2.5 sm:w-40 flex-shrink-0">
                                        {ieSplitData.map(d => (
                                            <div key={d.name} className="flex items-center justify-between text-sm gap-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.fill }} />
                                                    <span className="text-slate-700 truncate">{d.name}</span>
                                                </div>
                                                <span className="font-semibold text-slate-900 tabular-nums flex-shrink-0">{ieSplitTotal > 0 ? Math.round((d.value / ieSplitTotal) * 100) : 0}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                {ieChartType === "bar" ? (
                                    <BarChart data={series} margin={{ left: -12, right: 8, top: 8, bottom: 0 }} barCategoryGap="24%">
                                        <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }} formatter={(v) => fmtCurrency(v, cur)} />
                                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                        <Bar dataKey="income" name="Income" fill="#10B981" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="expense" name="Expense" fill="#6366F1" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                ) : ieChartType === "line" ? (
                                    <LineChart data={series} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                                        <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }} formatter={(v) => fmtCurrency(v, cur)} />
                                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                        <Line type="monotone" dataKey="income" name="Income" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3.5, strokeWidth: 2, fill: "#fff" }} />
                                        <Line type="monotone" dataKey="expense" name="Expense" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 3.5, strokeWidth: 2, fill: "#fff" }} />
                                    </LineChart>
                                ) : (
                                    <AreaChart data={series} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="g-income" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                                                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="g-expense" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                                                <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }} formatter={(v) => fmtCurrency(v, cur)} />
                                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                        <Area type="monotone" dataKey="income" name="Income" stroke="#10B981" fill="url(#g-income)" strokeWidth={2.5} />
                                        <Area type="monotone" dataKey="expense" name="Expense" stroke="#6366F1" fill="url(#g-expense)" strokeWidth={2.5} />
                                    </AreaChart>
                                )}
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Spending by category */}
                <div className="card-elev rounded-2xl p-6 animate-fade-up delay-300" data-testid="category-chart">
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Spending breakdown</h3>
                    <p className="text-sm text-slate-500 mb-4">By category, this period</p>
                    {catData.length === 0 ? (
                        <EmptyState icon={Receipt} title="No spending yet" description="Add an expense to see your breakdown." />
                    ) : (
                        <>
                            <div className="h-44 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={catData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                                            {catData.map((entry, i) => (
                                                <Cell key={i} fill={CATEGORY_COLORS[entry.name] || ["#6366F1","#10B981","#0EA5E9","#F59E0B","#EF4444","#8B5CF6"][i % 6]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmtCurrency(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span className="text-xs text-slate-500">Total</span>
                                    <span className="font-display text-xl font-extrabold text-slate-900">{fmtCompact(totalCat, cur)}</span>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                {catData.slice(0, 5).map((c, i) => (
                                    <div key={c.name} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[c.name] || ["#6366F1","#10B981","#0EA5E9","#F59E0B","#EF4444"][i % 5] }} />
                                            <span className="text-slate-700 truncate">{c.name}</span>
                                        </div>
                                        <span className="font-semibold text-slate-900 tabular-nums">{fmtCurrency(c.value, cur)}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Lower row: upcoming renewals + upcoming bills + recents */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Upcoming subscriptions */}
                <div className="card-elev rounded-2xl p-6 animate-fade-up delay-200" data-testid="upcoming-subs">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Upcoming renewals</h3>
                            <p className="text-sm text-slate-500">{fmtCurrency(data?.monthly_subscription_cost, cur)} / month</p>
                        </div>
                        <Link to="/subscriptions" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                    </div>
                    {(data?.upcoming_renewals || []).length === 0 ? (
                        <EmptyState icon={RefreshCw} title="No upcoming renewals" />
                    ) : (
                        <div className="space-y-2.5">
                            {(data?.upcoming_renewals || []).slice(0, 5).map(s => {
                                const dleft = daysUntil(s.renewal_date);
                                return (
                                    <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                        <span className="w-9 h-9 rounded-xl grid place-items-center text-white text-xs font-bold" style={{ backgroundColor: s.color || "#4F46E5" }}>
                                            {s.name.slice(0,1)}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                                {s.name}
                                                {isMember && scope === "household" && s.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(s.user_id)}</span>
                                                ) : null}
                                            </div>
                                            <div className="text-xs text-slate-500">{dleft === 0 ? "Today" : dleft === 1 ? "Tomorrow" : `In ${dleft}d`} · {fmtDate(s.renewal_date)}</div>
                                        </div>
                                        <div className="font-display font-bold text-sm text-slate-900 tabular-nums">{fmtCurrency(s.cost, cur)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Upcoming bills — recurring expenses with a next due date, within 15 days */}
                <div className="card-elev rounded-2xl p-6 animate-fade-up delay-250" data-testid="dash-bills">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Upcoming bills</h3>
                            <p className="text-sm text-slate-500">Due within 15 days</p>
                        </div>
                        <Link to="/expenses" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                    </div>
                    {(data?.upcoming_bills || []).length === 0 ? (
                        <EmptyState icon={Receipt} title="No bills due" description="Recurring expenses with a due date will show up here." />
                    ) : (
                        <div className="space-y-2.5" data-testid="dash-bills-list">
                            {(data?.upcoming_bills || []).slice(0, 5).map(b => {
                                const dleft = daysUntil(b.next_due_date);
                                const overdue = dleft < 0;
                                return (
                                    <div key={b.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                        <span className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${overdue ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                                            <Receipt className="w-4 h-4" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                                {b.title}
                                                {isMember && scope === "household" && b.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(b.user_id)}</span>
                                                ) : null}
                                            </div>
                                            <div className={`text-xs ${overdue ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                                                {overdue ? `Overdue ${Math.abs(dleft)}d` : dleft === 0 ? "Today" : dleft === 1 ? "Tomorrow" : `In ${dleft}d`} · {fmtDate(b.next_due_date)}
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="font-display font-bold text-sm text-slate-900 tabular-nums">{fmtCurrency(b.amount, cur)}</div>
                                            <button
                                                onClick={() => markBillPaid(b.id)}
                                                disabled={markingPaid === b.id}
                                                data-testid={`mark-paid-${b.id}`}
                                                className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                                            >
                                                {markingPaid === b.id ? "Saving…" : "Mark paid"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Recent transactions */}
                <div className="card-elev rounded-2xl p-6 animate-fade-up delay-300" data-testid="recent-tx">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Recent transactions</h3>
                            <p className="text-sm text-slate-500">Latest activity</p>
                        </div>
                        <Link to="/transactions" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                    </div>
                    {(data?.recent_transactions || []).length === 0 ? (
                        <EmptyState icon={Receipt} title="No transactions yet" description="Add your first expense or income." />
                    ) : (
                        <div className="space-y-2.5">
                            {(data?.recent_transactions || []).slice(0, 5).map(t => {
                                const isIncome = t.type === "income";
                                return (
                                    <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                        <span className={`w-9 h-9 rounded-xl grid place-items-center ${isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                            {isIncome ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                                {t.title}
                                                {isMember && scope === "household" && t.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(t.user_id)}</span>
                                                ) : null}
                                            </div>
                                            <div className="text-xs text-slate-500">{t.category} · {fmtRelative(t.occurred_at)}</div>
                                        </div>
                                        <div className={`font-display font-bold text-sm tabular-nums ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>
                                            {isIncome ? "+" : "−"}{fmtCurrency(t.amount, cur)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Savings goals — sinking funds, list view (moved down from the old Upcoming-bills full-width slot) */}
            <div className="card-elev rounded-2xl p-6 animate-fade-up delay-400" data-testid="dash-funds">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display text-lg font-bold text-slate-900">Savings goals</h3>
                        <p className="text-sm text-slate-500">Sinking funds</p>
                    </div>
                    <Link to="/funds" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                </div>
                {!canViewPro ? (
                    <EmptyState icon={Target} title="Sinking Funds is a Pro feature" description="Upgrade to Pro to save toward goals." />
                ) : (data?.sinking_funds || []).length === 0 ? (
                    <EmptyState icon={Target} title="No goals yet" description="Create a sinking fund to start saving." />
                ) : (
                    <div className="space-y-2.5" data-testid="dash-funds-list">
                        {(data?.sinking_funds || []).slice(0, 5).map(f => {
                            const goal = parseFloat(f.goal_amount);
                            const cur_amt = parseFloat(f.current_amount);
                            const pct = goal > 0 ? Math.min(100, (cur_amt/goal)*100) : 0;
                            return (
                                <div key={f.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                    <span className="w-9 h-9 rounded-xl grid place-items-center text-white flex-shrink-0" style={{ backgroundColor: f.color || "#4F46E5" }}>
                                        <Target className="w-4 h-4" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                            {f.name}
                                            {isMember && scope === "household" && f.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(f.user_id)}</span>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {Math.round(pct)}% funded{f.due_date ? ` · due ${fmtDate(f.due_date)}` : ""}
                                        </div>
                                    </div>
                                    <div className="font-display font-bold text-sm text-slate-900 tabular-nums flex-shrink-0">{fmtCompact(cur_amt, cur)}/{fmtCompact(goal, cur)}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Budget overview */}
            {budgets.length > 0 && (
                <div className="card-elev rounded-2xl p-6 animate-fade-up delay-400" data-testid="dash-budgets">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold text-slate-900">Budgets</h3>
                            <p className="text-sm text-slate-500">Category spending limits</p>
                        </div>
                        <Link to="/budgets" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                    </div>
                    <div className="space-y-1" data-testid="dash-budgets-list">
                        {budgets.map((b, i) => {
                            const limit = parseFloat(b.limit_amount) || 0;
                            const spent = parseFloat(b.spent) || 0;
                            const pct = Math.min(100, b.percent || 0);
                            const over = spent > limit;
                            const nearLimit = !over && b.percent >= 80;
                            const { icon: BudgetCatIcon, color: iconColor } = iconForCategoryName(categories, b.category);
                            // Progress bar / percentage stay status-colored (red/amber when over or near limit);
                            // the icon swatch always shows the category's own true color so it stays recognizable.
                            const color = over ? "#EF4444" : nearLimit ? "#F59E0B" : iconColor;
                            return (
                                <Link key={b.id} to="/budgets" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                    <span className="w-9 h-9 rounded-xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${iconColor}18`, color: iconColor }}>
                                        <BudgetCatIcon className="w-4 h-4" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-semibold text-sm text-slate-900 truncate">{b.category}</span>
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 flex-shrink-0">{b.period}</span>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="font-display font-bold text-sm text-slate-900 tabular-nums">{fmtCompact(spent, cur)}/{fmtCompact(limit, cur)}</div>
                                        <div className={`text-xs font-semibold ${over ? "text-rose-600" : nearLimit ? "text-amber-600" : "text-slate-400"}`}>
                                            {Math.round(b.percent)}%
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Investments overview */}
            {investments.length > 0 && (() => {
                const totalInvested = investments.reduce((s, a) => s + parseFloat(a.current_value), 0);
                const totalChange = investments.reduce((s, a) => s + parseFloat(a.change_amount), 0);
                const initial = totalInvested - totalChange;
                const pct = initial !== 0 ? (totalChange / initial) * 100 : 0;
                const up = totalChange >= 0;
                return (
                    <div className="card-elev rounded-2xl p-6 animate-fade-up delay-400" data-testid="dash-investments">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-display text-lg font-bold text-slate-900">Investments</h3>
                                <p className="text-sm text-slate-500">
                                    {fmtCurrency(totalInvested, cur)} total
                                    <span className={`ml-1.5 font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                                        {up ? "+" : ""}{fmtCurrency(totalChange, cur)} ({up ? "+" : ""}{pct.toFixed(1)}%)
                                    </span>
                                </p>
                            </div>
                            <Link to="/investments" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View all</Link>
                        </div>
                        <div className="space-y-1" data-testid="dash-investments-list">
                            {investments.map(a => {
                                const meta = accountTypeMeta(a.account_type);
                                const Icon = meta.icon;
                                const change = parseFloat(a.change_amount);
                                const accUp = change >= 0;
                                return (
                                    <Link key={a.id} to={`/investments/${a.id}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                        <span className="w-9 h-9 rounded-xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${a.color || meta.color}18`, color: a.color || meta.color }}>
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                                {a.name}
                                                {isMember && scope === "household" && a.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(a.user_id)}</span>
                                                ) : null}
                                            </div>
                                            <div className="text-xs text-slate-500">{meta.label}</div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="font-display font-bold text-sm text-slate-900 tabular-nums">{fmtCurrency(a.current_value, cur)}</div>
                                            <div className={`text-xs font-semibold ${a.entry_count > 0 ? (accUp ? "text-emerald-600" : "text-rose-600") : "text-slate-400"}`}>
                                                {a.entry_count > 0 ? `${accUp ? "+" : ""}${a.change_percent.toFixed(1)}%` : "No data"}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {loading && (
                <div className="text-center text-xs text-slate-400 py-2">Refreshing…</div>
            )}
        </div>
    );
}
